/**
 * Custom hook for file upload functionality
 */

import { useState, useCallback, useRef } from 'react';
import { validateFileForUpload } from '@/lib/utils/document-utils';
import { FILE_UPLOAD_CONSTRAINTS } from '@/lib/config/document-types';
// Canonical `UploadFile` now lives in the shared FileDropZone package
// (LF-4771 type inversion). Re-exported here so existing importers of
// `@/hooks/use-file-upload` keep working unchanged.
import type { UploadFile } from '@/components/ui/file-drop-zone/types';

export type { UploadFile };

/**
 * Per-file plumbing handed to `onUpload` so the caller drives REAL upload
 * progress (LF-4771 — replaces the old simulated `setInterval`). `onFileProgress`
 * is keyed by the `File` object (stable across the dialog mapping layers, which
 * drop the row id), so a caller that only has the `File` can still update its
 * row. `signal` aborts the in-flight batch.
 */
export interface FileUploadHelpers {
  onFileProgress: (file: File, pct: number) => void;
  signal: AbortSignal;
}

interface UseFileUploadOptions {
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  onUpload?: (files: UploadFile[], helpers: FileUploadHelpers) => Promise<void>;
  autoDetectFileType?: boolean;
}

interface UseFileUploadReturn {
  // File state
  files: UploadFile[];

  // File operations
  addFiles: (newFiles: File[]) => void;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;

  // Upload operations
  uploadFiles: () => Promise<void>;
  isUploading: boolean;
  uploadProgress: number;

  // File type operations
  updateFileType: (fileId: string, fileType: string) => void;
  updateCertifiedCopy: (fileId: string, isCertified: boolean) => void;

  // Validation
  validateFiles: (files: File[]) => {
    valid: File[];
    invalid: Array<{ file: File; error: string }>;
  };

  // Utilities
  hasFiles: boolean;
  canUpload: boolean;
  totalSize: number;
  successCount: number;
  errorCount: number;
}

export function useFileUpload({
  maxFiles,
  maxFileSize = FILE_UPLOAD_CONSTRAINTS.maxFileSize,
  acceptedFileTypes,
  onUpload,
  autoDetectFileType = true
}: UseFileUploadOptions = {}): UseFileUploadReturn {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileIdCounterRef = useRef(0);

  // Generate unique file ID
  const generateFileId = () => {
    fileIdCounterRef.current += 1;
    return `file-${Date.now()}-${fileIdCounterRef.current}`;
  };

  // Validate files before adding
  const validateFiles = useCallback(
    (filesToValidate: File[]) => {
      const valid: File[] = [];
      const invalid: Array<{ file: File; error: string }> = [];

      filesToValidate.forEach((file) => {
        /*
          A batch limit only if the caller asked for one (VALE-1586).

          `undefined` means no limit, and that is the default. A cap here is a
          statement about a particular DOCUMENT TYPE — a grant of representation
          is one document, so its dialog passes 1 — not about how many files a
          person may hand over at once. The add-documents dialog used to inherit
          a generic 10 that no other upload surface had and that nothing
          downstream needed: uploads are per-file presigned S3 PUTs, so no
          request ever carries a batch.
        */
        if (maxFiles !== undefined && files.length + valid.length >= maxFiles) {
          invalid.push({ file, error: `Maximum ${maxFiles} files allowed` });
          return;
        }

        // Validate file size
        const sizeValidation = validateFileForUpload(file, maxFileSize);
        if (!sizeValidation.valid) {
          invalid.push({ file, error: sizeValidation.error! });
          return;
        }

        // Check file type if specified
        if (acceptedFileTypes && acceptedFileTypes.length > 0) {
          const fileExtension = file.name.split('.').pop()?.toLowerCase();
          if (!fileExtension) {
            invalid.push({ file, error: 'File must have an extension' });
            return;
          }
          if (!acceptedFileTypes.includes(fileExtension)) {
            invalid.push({
              file,
              error: `File type .${fileExtension} not accepted`
            });
            return;
          }
        }

        valid.push(file);
      });

      return { valid, invalid };
    },
    [files.length, maxFiles, maxFileSize, acceptedFileTypes]
  );

  // Add files to upload queue
  const addFiles = useCallback(
    (newFiles: File[]) => {
      const { valid } = validateFiles(newFiles);

      const uploadFiles = valid.map((file) => {
        const fileType = autoDetectFileType
          ? detectFileTypeFromFilename(file.name)
          : 'additional-document';

        return {
          file,
          id: generateFileId(),
          progress: 0,
          status: 'pending' as const,
          fileType,
          isCertifiedCopy: false
        };
      });

      setFiles((current) => [...current, ...uploadFiles]);
    },
    [validateFiles, autoDetectFileType]
  );

  // Remove file from queue
  const removeFile = useCallback((fileId: string) => {
    setFiles((current) => current.filter((f) => f.id !== fileId));
  }, []);

  // Clear all files
  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  // Update file type
  const updateFileType = useCallback((fileId: string, fileType: string) => {
    setFiles((current) =>
      current.map((f) => (f.id === fileId ? { ...f, fileType } : f))
    );
  }, []);

  // Update certified copy status
  const updateCertifiedCopy = useCallback(
    (fileId: string, isCertified: boolean) => {
      setFiles((current) =>
        current.map((f) =>
          f.id === fileId ? { ...f, isCertifiedCopy: isCertified } : f
        )
      );
    },
    []
  );

  // Upload files
  const uploadFiles = useCallback(async () => {
    if (!onUpload || files.length === 0) return;

    setIsUploading(true);
    const controller = new AbortController();

    try {
      // All queued files start uploading at 0%.
      setFiles((current) =>
        current.map((f) => ({
          ...f,
          status: 'uploading' as const,
          progress: 0,
          error: undefined
        }))
      );

      // Real progress (LF-4771): the caller drives each row via
      // `onFileProgress(file, pct)` as its per-file PUT reports bytes — no more
      // simulated `setInterval`. Callers that ignore the helpers simply leave
      // rows at 0% until the batch resolves to 100%.
      await onUpload(files, {
        onFileProgress: (file, pct) => {
          const clamped = Math.min(Math.max(pct, 0), 100);
          setFiles((current) =>
            current.map((f) =>
              f.file === file ? { ...f, progress: clamped } : f
            )
          );
        },
        signal: controller.signal
      });

      // Mark all as success
      setFiles((current) =>
        current.map((f) => ({
          ...f,
          status: 'success' as const,
          progress: 100
        }))
      );
    } catch (error) {
      // Mark all as error
      setFiles((current) =>
        current.map((f) => ({
          ...f,
          status: 'error' as const,
          error: error instanceof Error ? error.message : 'Upload failed'
        }))
      );
    } finally {
      setIsUploading(false);
    }
  }, [files, onUpload]);

  // Computed properties
  const hasFiles = files.length > 0;
  const canUpload =
    hasFiles && !isUploading && files.some((f) => f.status === 'pending');
  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
  const successCount = files.filter((f) => f.status === 'success').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  const uploadProgress =
    files.length > 0
      ? files.reduce((sum, f) => sum + f.progress, 0) / files.length
      : 0;

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    uploadFiles,
    isUploading,
    uploadProgress,
    updateFileType,
    updateCertifiedCopy,
    validateFiles,
    hasFiles,
    canUpload,
    totalSize,
    successCount,
    errorCount
  };
}

/**
 * Detect file type from filename
 */
function detectFileTypeFromFilename(filename: string): string {
  const lower = filename.toLowerCase();

  // Check for specific patterns matching backend file types
  if (lower.includes('will') || lower.includes('testament')) return 'will';
  if (lower.includes('death') && lower.includes('certificate'))
    return 'death-certificate';
  if (
    lower.includes('grant') ||
    lower.includes('probate') ||
    lower.includes('representation')
  )
    return 'grant-of-representation';
  if (
    lower.includes('id') ||
    lower.includes('passport') ||
    lower.includes('license') ||
    lower.includes('identification')
  )
    return 'primary-id';
  // IMPORTANT: Never return 'correspondence-attachment' as it requires attachmentId
  // Map general correspondence/letters to additional-document instead
  if (
    lower.includes('letter') ||
    lower.includes('email') ||
    lower.includes('correspondence')
  )
    return 'additional-document';
  if (lower.includes('letterhead')) return 'letterhead';
  if (lower.includes('signature')) {
    if (lower.includes('email')) return 'email-signature';
    return 'handwritten-signature';
  }
  if (lower.includes('institution') || lower.includes('form')) {
    if (lower.includes('signed')) return 'institution-form-signed';
    if (lower.includes('template')) return 'institution-form-template';
    // Default to signed form for general form documents
    return 'institution-form-signed';
  }

  return 'additional-document'; // Default to additional-document for general matter files
}
